import React, { FC, useEffect } from "react";

import log from "loglevel";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import Typography from "@mui/material/Typography";

import { EmptyBrokerList } from "@/assets/images/login";
import { BackdropWithSpinner } from "@/components/atoms";
import NotAuthenticatedHeader from "@/components/layouts/NotAuthenticated/components/NotAuthenticatedHeader/NotAuthenticatedHeader";
import { brokerApi } from "@/shared/apis";
import { UrlManager } from "@/shared/apis/UrlManager";
import { useAuth, useNavigation, useRouterHook } from "@/shared/hooks";

import {
    StyledBrokerItem,
    StyledBrokerName,
    StyledBrokersContainer,
    StyledChooseBrokerPage,
    StyledEmptyContainer,
    StyledImage,
    StyledImageContainer,
    StyledTitle,
} from "./styled";

export type ChooseBrokerProps = {
    email: string;
    fromItsMe?: boolean;
};

const ChooseBroker: FC<ChooseBrokerProps> = ({ email, fromItsMe = false }) => {
    const {
        globalState: { officeId },
    } = useRouterHook();
    const { loginSilent } = useAuth();
    const { t } = useTranslation();
    const { navToSearchPathname } = useNavigation();

    const { mutate: handleBrokerSelected, isLoading: isLoggingIn } = useMutation(
        "login",
        async (broker: MyBrokerApi.Broker) => {
            log.info("handleBrokerSelected", { email, broker: broker.officeId });
            await loginSilent(email, broker.officeId, fromItsMe);
        },
        {
            onSuccess: () => {
                navToSearchPathname();
            },
        },
    );

    const { data: brokerRequestData, isLoading: isLoadingBrokers } = brokerApi.useQueryBrokers({
        placeholderData: undefined,
    });

    const brokers = brokerRequestData?.result || [];

    useEffect(() => {
        const findBroker = brokers.find((broker) => broker.officeId === officeId);
        if (findBroker) {
            handleBrokerSelected(findBroker);
        } else if (brokers.length === 1) {
            handleBrokerSelected(brokers[0]);
        }
    }, [brokers]);

    const handleKeyDown = (event: React.KeyboardEvent, broker: MyBrokerApi.Broker) => {
        if (event.key === "Enter") {
            handleBrokerSelected(broker);
        }
    };

    const isLoading = isLoggingIn || isLoadingBrokers;

    return (
        <StyledChooseBrokerPage data-testid="choose-broker-step">
            {isLoading && <BackdropWithSpinner />}
            {!isLoading && (
                <>
                    <NotAuthenticatedHeader light lightMenu />
                    <Helmet>
                        <title>{`MyBroker - ${t("Common.Broker")}`}</title>
                    </Helmet>
                    {brokers.length === 0 && (
                        <StyledEmptyContainer>
                            <EmptyBrokerList />

                            <Typography fontSize="var(--size-24)" color="var(--color-primary)" fontWeight="bold">
                                {t("Errors.Title_NoBroker")}
                            </Typography>
                            <Typography
                                fontSize={{ xs: "var(--size-14)", md: "var(--size-16)" }}
                                color="var(--color-grey-900)"
                                textAlign="center"
                            >
                                {t("Errors.Message_NoBroker")}
                            </Typography>
                        </StyledEmptyContainer>
                    )}
                    {brokers.length > 0 && (
                        <>
                            <StyledTitle className="title">{t("Login.ChooseBroker.Title")}</StyledTitle>
                            <StyledBrokersContainer>
                                {brokers.map((broker) => {
                                    return (
                                        <StyledBrokerItem
                                            role="button"
                                            tabIndex={0}
                                            data-testid={`choose_broker-item-${broker.officeId}`}
                                            key={broker.officeId}
                                            onClick={() => handleBrokerSelected(broker)}
                                            onKeyDown={(event) => handleKeyDown(event, broker)}
                                        >
                                            <StyledImageContainer>
                                                <StyledImage>
                                                    <img src={UrlManager.brokerLogo(broker.officeId)} alt="" />
                                                </StyledImage>
                                            </StyledImageContainer>
                                            <StyledBrokerName>{broker.brokerName}</StyledBrokerName>
                                        </StyledBrokerItem>
                                    );
                                })}
                            </StyledBrokersContainer>
                        </>
                    )}
                </>
            )}
        </StyledChooseBrokerPage>
    );
};

export default ChooseBroker;
